/* ==========================================================================
 * Kimes Ranch — pause controls for content that moves on its own
 * --------------------------------------------------------------------------
 * WCAG 2.2.2 (Pause, Stop, Hide). Two things in Impact start moving without
 * the user asking and give no way to stop:
 *
 *   1. Video sections set to autoplay. Impact passes `controls: false` and
 *      `loop: true`, so the native controls are gone.
 *   2. The announcement bar, which auto-rotates every few seconds sitewide.
 *      Its prev/next arrows are navigation, not a pause.
 *
 * Both get the same button (snippets/kr-pause-button.liquid); only the
 * binding differs, so the shared behaviour lives in KrPauseControl and each
 * subclass supplies target/play/pause/isPlaying.
 *
 * Both targets restart themselves when scrolled back into view, so an
 * explicit pause has to be latched or it would silently undo itself.
 * ========================================================================== */

class KrPauseControl extends HTMLElement {
  async connectedCallback() {
    this.button = this.querySelector('button');
    this.label = this.querySelector('[data-kr-pause-label]');
    // Both targets are custom elements defined in theme.js. This script is
    // deferred and cannot assume they have upgraded yet, so findTarget awaits.
    this.target = await this.findTarget();

    // A control that cannot reach its target is worse than no control at all —
    // it is a focusable element that does nothing. Remove it instead.
    if (!this.target || !this.button || !this.label) {
      this.remove();
      return;
    }

    this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.button.addEventListener('click', this.onClick);
    this.listen();

    if (this.userPaused) {
      this.pauseTarget();
    }
    this.sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    this.abort?.abort();
  }

  onClick = () => {
    this.userPaused = this.isPlaying();
    this.userPaused ? this.pauseTarget() : this.playTarget();
    this.sync();
  };

  sync = () => {
    const playing = this.isPlaying();
    // The sr-only span IS the accessible name — no aria-label, so the name is
    // right even before this script runs.
    this.toggleAttribute('paused', !playing);
    this.label.textContent = this.button.dataset[playing ? 'pauseLabel' : 'playLabel'];
  };
}

/* <video-media> — BaseMedia exposes play()/pause(), a [playing] attribute and
 * bubbling media:play / media:pause events. Autoplay replays it via inView on
 * every scroll re-entry, so the latch is enforced by re-pausing on play. */
class KrVideoPause extends KrPauseControl {
  async findTarget() {
    const previous = this.previousElementSibling;
    if (!previous?.matches('video-media')) {
      return null;
    }
    await window.customElements.whenDefined('video-media');
    return previous;
  }

  listen() {
    this.abort = new AbortController();
    const opts = { signal: this.abort.signal };
    this.target.addEventListener('media:play', () => {
      if (this.userPaused) {
        this.pauseTarget();
        return;
      }
      this.sync();
    }, opts);
    this.target.addEventListener('media:pause', this.sync, opts);
  }

  isPlaying() {
    return this.target.playing;
  }

  playTarget() {
    this.target.play();
  }

  pauseTarget() {
    this.target.pause();
  }
}

/* <announcement-bar> extends EffectCarousel, which exposes a Player. Player
 * .resume() early-returns while the state is "stopped", so stop() is itself
 * the latch — the carousel's inView resume cannot undo it. */
class KrAnnouncementPause extends KrPauseControl {
  async findTarget() {
    const bar = this.parentElement?.querySelector('announcement-bar');
    if (!bar) {
      return null;
    }
    await window.customElements.whenDefined('announcement-bar');
    // EffectCarousel only builds a Player when it actually autoplays more than
    // one slide. No player means nothing moves, so the control is not needed.
    return bar.player ?? null;
  }

  listen() {
    this.abort = new AbortController();
    const opts = { signal: this.abort.signal };
    ['player:start', 'player:resume', 'player:stop', 'player:pause'].forEach(
      (name) => this.target.addEventListener(name, this.sync, opts)
    );
  }

  isPlaying() {
    return !this.userPaused;
  }

  playTarget() {
    this.target.start();
  }

  pauseTarget() {
    this.target.stop();
  }
}

if (!window.customElements.get('kr-video-pause')) {
  window.customElements.define('kr-video-pause', KrVideoPause);
}

if (!window.customElements.get('kr-announcement-pause')) {
  window.customElements.define('kr-announcement-pause', KrAnnouncementPause);
}

/* Pause the existing automatic scrolling text without changing its content. */
class KrMarqueePause extends KrPauseControl {
  async findTarget() {
    const target = this.previousElementSibling;
    return target?.querySelector('marquee-text') ? target : null;
  }
  listen() {}
  isPlaying() { return !this.target.hasAttribute('data-kr-paused'); }
  playTarget() { this.target.removeAttribute('data-kr-paused'); }
  pauseTarget() { this.target.setAttribute('data-kr-paused', ''); }
}
if (!customElements.get('kr-marquee-pause')) customElements.define('kr-marquee-pause', KrMarqueePause);
